import { getColorFromName } from "@/components/elements/PerformanceChart/utils";

import type { AuctionCarTransaction, AuctionsChartPayload } from "@/types/auctions";
import type { TFunction } from "i18next";

type TransactionRecord = Pick<AuctionCarTransaction, "outlier" | "replica">;
type ChartRecord = Pick<AuctionsChartPayload, "condition" | "outlier" | "replica">;

export function isTransactionOutlier(record: TransactionRecord | ChartRecord) {
  return record.outlier === true;
}

export function isTransactionReplica(record: TransactionRecord | ChartRecord) {
  return record.replica === true;
}

export function isExcludedTransactionRecord(record: TransactionRecord | ChartRecord) {
  return isTransactionOutlier(record) || isTransactionReplica(record);
}

export function isPriceOnApplication(value?: unknown) {
  if (value == null) return true;

  if (typeof value === "number") {
    return value === 0;
  }

  const normalized = String(value).trim().toLowerCase();
  return (
    normalized === "" ||
    normalized === "0" ||
    normalized === "not_available" ||
    normalized === "not available" ||
    normalized === "poa" ||
    normalized === "price_on_application"
  );
}

export function shouldHideTransactionPrice(_transaction: TransactionRecord, value?: unknown) {
  return isPriceOnApplication(value);
}

export function getTransactionNotAvailableLabel(t: TFunction, showDash?: boolean) {
  if (showDash) {
    return "-";
  }

  return t("components.Auctions.notAvailable");
}

export function getPriceOnApplicationLabel(t: TFunction) {
  return t("components.Auctions.poa");
}

export function getListingPriceLabel(
  t: TFunction,
  _transaction: TransactionRecord,
  value?: unknown
) {
  if (isPriceOnApplication(value)) {
    return getPriceOnApplicationLabel(t);
  }

  return null;
}

export function getTransactionId(transaction: AuctionCarTransaction): string {
  if (transaction.id) return transaction.id;

  return [
    transaction.date,
    transaction.vin,
    transaction.hammer_price,
    transaction.auction_house,
    transaction.event,
    transaction.type,
  ]
    .map((value) => String(value ?? ""))
    .join("|");
}

export function getChartPointColor(record: ChartRecord, fallback = "#eeb91d") {
  return getColorFromName(record.condition ?? "") ?? fallback;
}

export type ChartPointShape = "rectangle" | "square" | "triangle";

export function getChartPointShape(
  record: Pick<AuctionsChartPayload, "outlier" | "replica">
): ChartPointShape {
  if (record.replica) return "square";
  if (record.outlier) return "triangle";
  return "rectangle";
}
